import React, { useEffect, useRef, useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, Fab, FormControl, InputLabel, MenuItem, Select, Snackbar, Tooltip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PageHeader from '../components/layout/PageHeader';
import DocumentList from '../components/documents/DocumentList';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import usePageTitle from '../hooks/usePageTitle';
import { Document, getDocuments, uploadDocument, updateDocumentTopic, deleteDocument } from '../services/documents';
import { Topic, getTopics } from '../services/topics';

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB

const DocumentsPage: React.FC = () => {
  usePageTitle('Documents');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const [topicTarget, setTopicTarget] = useState<Document | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<string>('');
  const [showFab, setShowFab] = useState(false);
  const [errorSnack, setErrorSnack] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [docRes, topicRes] = await Promise.all([getDocuments(), getTopics()]);
      setDocuments(docRes.data);
      setTopics(topicRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setErrorSnack('File too large. Maximum size is 1 MB.');
      if (fileInput.current) fileInput.current.value = '';
      return;
    }
    try {
      await uploadDocument(file);
      load();
    } catch (err: any) {
      if (err?.response?.status === 413) {
        setErrorSnack('File too large. Maximum size is 1 MB.');
      } else {
        console.error('Upload failed', err);
      }
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDocument(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const handleTopicChange = (doc: Document) => {
    setTopicTarget(doc);
    setSelectedTopicId(doc.topic_id || '');
  };

  const handleTopicSave = async () => {
    if (!topicTarget) return;
    const docId = topicTarget.id;
    const newTopicId = selectedTopicId || null;
    // Optimistic update
    setDocuments((prev) => prev.map((d) => d.id === docId ? { ...d, topic_id: newTopicId } : d));
    setTopicTarget(null);
    try {
      await updateDocumentTopic(docId, newTopicId);
    } catch (err) {
      console.error(err);
      // Revert on error
      const res = await getDocuments();
      setDocuments(res.data);
    }
  };

  return (
    <>
      <input ref={fileInput} type="file" hidden onChange={handleUpload} />
      <div ref={headerRef}>
        <PageHeader
          title="Documents"
          actionLabel="Upload File"
          onAction={() => fileInput.current?.click()}
          actionIcon={<CloudUploadIcon />}
        />
      </div>
      {loading ? <LoadingSpinner /> : (
        <DocumentList
          documents={documents}
          topics={topics}
          onDelete={setDeleteTarget}
          onTopicChange={handleTopicChange}
        />
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteTarget?.filename}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      {/* Topic assignment dialog */}
      <Dialog open={!!topicTarget} onClose={() => setTopicTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Topic</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Topic</InputLabel>
            <Select
              value={selectedTopicId}
              label="Topic"
              onChange={(e) => setSelectedTopicId(e.target.value)}
              onClose={() => {
                // Auto-save on close for quick UX
                setTimeout(() => {
                  if (topicTarget) handleTopicSave();
                }, 100);
              }}
            >
              <MenuItem value="">None</MenuItem>
              {topics.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.emoji && `${t.emoji} `}{t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={!!errorSnack}
        autoHideDuration={4000}
        onClose={() => setErrorSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorSnack('')} variant="filled">
          {errorSnack}
        </Alert>
      </Snackbar>
      {showFab && (
        <Tooltip title="Upload File">
          <Fab
            color="primary"
            onClick={() => fileInput.current?.click()}
            sx={{ position: 'fixed', bottom: 24, right: 24 }}
          >
            <CloudUploadIcon />
          </Fab>
        </Tooltip>
      )}
    </>
  );
};

export default DocumentsPage;
